/**
 * A line in the record the phone keeps on the server.
 *
 * The interesting failures here are all in the platform — speech that never speaks, a location
 * watch that never fires — and none of them leave a trace anywhere else. Fire and forget: a
 * diagnostic that breaks the thing it is watching is worse than no diagnostic at all.
 */

import { getDeviceId } from "./useDeviceId";
import { shellBuild, shellFetch } from "./native";
import { nextReportTime } from "../core/diagnostic-order";

let lastReportAtMs = 0;

export function report(event: string, detail: Record<string, unknown>): void {
	void send(event, detail).catch(() => undefined);
}

/**
 * The same write, but answering whether it landed.
 *
 * Recording is refused for a device with no saved alert, which is silent by design and so
 * indistinguishable from working. Pressing the button in the debug panel should say which.
 */
export async function sendReport(event: string, detail: Record<string, unknown>): Promise<string> {
	try {
		const response = await send(event, detail);
		if (response.ok) {
			return "sent";
		}
		const body = (await response.json().catch(() => null)) as { error?: { code?: string; message?: string } } | null;
		return body?.error?.message ?? body?.error?.code ?? `refused (${response.status})`;
	} catch (cause) {
		return `unreachable: ${cause instanceof Error ? cause.message : String(cause)}`;
	}
}

function send(event: string, detail: Record<string, unknown>): Promise<Response> {
	// Stamped on every row rather than on the heartbeat alone: a phone that only managed a chime is
	// still one of two GoTimes on that handset, and reading it back as a browser is worse than
	// reading it back as unknown.
	const shell = shellBuild();
	let saved: unknown = null;
	try { saved = JSON.parse(localStorage.getItem("gotime.reportTime") ?? "null"); } catch {}
	lastReportAtMs = nextReportTime(lastReportAtMs, Date.now(), saved);
	try { localStorage.setItem("gotime.reportTime", JSON.stringify(lastReportAtMs)); } catch {}
	const stamped = { reportedAtMs: lastReportAtMs, ...detail };
	return shellFetch("/api/diagnostics", {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		keepalive: true,
		body: JSON.stringify({ deviceId: getDeviceId(), event, detail: shell ? { ...stamped, shell } : stamped }),
	});
}
